import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Navigation, X, Check, Route as RouteIcon, Menu, Save } from 'lucide-react';
import SaveRouteModal from '../routes/SaveRouteModal';
import LoadRouteDropdown from '../routes/LoadRouteDropdown';
import { saveRoute, updateRoute, getAllRoutes, getRoute, deleteRoute } from '../../../database/db';

// Sample job data - you can modify this or load from an API
const initialJobs = [
    {
        id: 1,
        name: "Harry Victor",
        time: "9:00-10:00",
        address: "1200 NW 15th Ave, Pompano Beach, FL 33069",
        phone: "954-614-3522",
        code: "M225",
        lat: 26.2479,
        lng: -80.1453
    },
    {
        id: 2,
        name: "Sarah Johnson",
        time: "10:30-11:30",
        address: "2500 E Commercial Blvd, Fort Lauderdale, FL 33308",
        phone: "954-123-4567",
        code: "M226",
        lat: 26.1887,
        lng: -80.1131
    },
    {
        id: 3,
        name: "Mike Davis",
        time: "12:00-13:00",
        address: "100 S Birch Rd, Fort Lauderdale, FL 33316",
        phone: "954-234-5678",
        code: "M227",
        lat: 26.1196,
        lng: -80.1065
    },
    {
        id: 4,
        name: "Emily Chen",
        time: "14:00-15:00",
        address: "1350 E Sunrise Blvd, Fort Lauderdale, FL 33304",
        phone: "954-345-6789",
        code: "M228",
        lat: 26.1265,
        lng: -80.1240
    }
];

const RoutesTab = () => {
    const [jobs, setJobs] = useState(initialJobs);
    const [selectedJob, setSelectedJob] = useState(null);
    const [optimizedRoute, setOptimizedRoute] = useState([]);
    const [isOptimized, setIsOptimized] = useState(false);
    const [apiKey, setApiKey] = useState('');
    const [showApiInput, setShowApiInput] = useState(true);
    const [currentLocation, setCurrentLocation] = useState({ lat: 26.2379, lng: -80.1248 });
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

    // Route saving/loading state
    const [showSaveModal, setShowSaveModal] = useState(false);
    const [savedRoutes, setSavedRoutes] = useState([]);
    const [currentRouteName, setCurrentRouteName] = useState(null);
    const [currentRouteId, setCurrentRouteId] = useState(null);

    const mapRef = useRef(null);
    const googleMapRef = useRef(null);
    const markersRef = useRef([]);
    const directionsRendererRef = useRef(null);
    const markerLibraryRef = useRef(null);

    // Initialize API key from config
    useEffect(() => {
        const checkConfig = () => {
            if (typeof window !== 'undefined' && window.CONFIG && window.CONFIG.GOOGLE_MAPS_API_KEY) {
                const configApiKey = window.CONFIG.GOOGLE_MAPS_API_KEY;
                console.log('API key loaded from config:', configApiKey.substring(0, 20) + '...');
                setApiKey(configApiKey);
                setShowApiInput(false);
                return true;
            }
            return false;
        };

        // Try immediately
        if (checkConfig()) return;

        console.log('Config not loaded yet, polling...');
        // If config not loaded yet, poll for it
        const interval = setInterval(() => {
            if (checkConfig()) {
                clearInterval(interval);
            }
        }, 100);

        // Cleanup after 5 seconds
        const timeout = setTimeout(() => {
            clearInterval(interval);
            console.log('Config polling timeout - API key may not be loaded');
        }, 5000);

        return () => {
            clearInterval(interval);
            clearTimeout(timeout);
        };
    }, []);

    // Initialize Google Map
    useEffect(() => {
        if (!apiKey || showApiInput) return;

        // Wait for Google Maps to load
        const checkGoogleMaps = setInterval(() => {
            if (window.google && window.google.maps) {
                clearInterval(checkGoogleMaps);
                initializeGoogleMap();
            }
        }, 100);

        // Cleanup interval after 10 seconds
        const timeout = setTimeout(() => {
            clearInterval(checkGoogleMaps);
        }, 10000);

        return () => {
            clearInterval(checkGoogleMaps);
            clearTimeout(timeout);
        };
    }, [apiKey, showApiInput, currentLocation]);

    // Get user's current location
    useEffect(() => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setCurrentLocation({
                        lat: position.coords.latitude,
                        lng: position.coords.longitude
                    });
                },
                (error) => {
                    console.log('Using default location');
                }
            );
        }
    }, []);

    // Load saved routes on mount
    useEffect(() => {
        loadSavedRoutes();
    }, []);

    const initializeGoogleMap = async () => {
        console.log('initializeGoogleMap called', {
            hasMapRef: !!mapRef.current,
            hasGoogle: !!window.google,
            currentLocation
        });

        if (!mapRef.current || !window.google) {
            console.log('Map initialization skipped - missing requirements');
            return;
        }

        console.log('Creating Google Map...');
        googleMapRef.current = new window.google.maps.Map(mapRef.current, {
            center: currentLocation,
            zoom: 11,
            mapTypeControl: true,
            streetViewControl: true,
            fullscreenControl: true,
            mapId: 'DEMO_MAP_ID' // Required for AdvancedMarkerElement
        });
        console.log('Google Map created successfully');

        // Load the Advanced Marker library
        try {
            const { AdvancedMarkerElement, PinElement } = await window.google.maps.importLibrary("marker");
            markerLibraryRef.current = { AdvancedMarkerElement, PinElement };
            console.log('Advanced Marker library loaded');

            // Add current location marker
            const currentLocationPin = new PinElement({
                background: '#4285F4',
                borderColor: '#FFFFFF',
                glyphColor: '#FFFFFF',
                scale: 1.2
            });

            new AdvancedMarkerElement({
                position: currentLocation,
                map: googleMapRef.current,
                title: 'Your Location',
                content: currentLocationPin.element
            });

            // Add job markers
            addMarkersToMap();
        } catch (error) {
            console.error('Error loading Advanced Marker library:', error);
        }
    };

    const addMarkersToMap = () => {
        if (!googleMapRef.current || !window.google || !markerLibraryRef.current) return;

        const { AdvancedMarkerElement, PinElement } = markerLibraryRef.current;

        // Clear existing markers
        markersRef.current.forEach(marker => marker.map = null);
        markersRef.current = [];

        jobs.forEach((job, index) => {
            // Create a custom pin with label
            const pinElement = new PinElement({
                background: '#16a34a', // green-600
                borderColor: '#FFFFFF',
                glyphColor: '#FFFFFF',
                glyph: isOptimized ? String(index + 1) : job.code
            });

            const marker = new AdvancedMarkerElement({
                position: { lat: job.lat, lng: job.lng },
                map: googleMapRef.current,
                title: job.name,
                content: pinElement.element
            });

            marker.addListener('click', () => {
                setSelectedJob(job);
                const infoWindow = new window.google.maps.InfoWindow({
                    content: `
                        <div style="padding: 10px;">
                            <h3 style="margin: 0 0 8px 0; font-size: 16px;">${job.name}</h3>
                            <p style="margin: 4px 0; font-size: 13px;"><strong>Time:</strong> ${job.time}</p>
                            <p style="margin: 4px 0; font-size: 13px;"><strong>Code:</strong> ${job.code}</p>
                            <p style="margin: 4px 0; font-size: 13px;">${job.address}</p>
                            <p style="margin: 4px 0; font-size: 13px;"><strong>Phone:</strong> ${job.phone}</p>
                        </div>
                    `
                });
                infoWindow.open(googleMapRef.current, marker);
            });

            markersRef.current.push(marker);
        });
    };

    // Calculate distance between two points using Haversine formula
    const calculateDistance = (lat1, lng1, lat2, lng2) => {
        const R = 6371; // Earth's radius in km
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLng = (lng2 - lng1) * Math.PI / 180;
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLng / 2) * Math.sin(dLng / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    };

    // Optimize route using nearest neighbor algorithm
    const optimizeRoute = () => {
        if (jobs.length === 0) return;

        const unvisited = [...jobs];
        const route = [];
        let current = currentLocation;

        while (unvisited.length > 0) {
            let nearestIndex = 0;
            let shortestDistance = calculateDistance(
                current.lat,
                current.lng,
                unvisited[0].lat,
                unvisited[0].lng
            );

            for (let i = 1; i < unvisited.length; i++) {
                const distance = calculateDistance(
                    current.lat,
                    current.lng,
                    unvisited[i].lat,
                    unvisited[i].lng
                );
                if (distance < shortestDistance) {
                    shortestDistance = distance;
                    nearestIndex = i;
                }
            }

            const nearest = unvisited.splice(nearestIndex, 1)[0];
            route.push(nearest);
            current = { lat: nearest.lat, lng: nearest.lng };
        }

        setOptimizedRoute(route);
        setJobs(route);
        setIsOptimized(true);

        // Draw route on map
        if (googleMapRef.current && window.google) {
            drawOptimizedRoute(route);
        }
    };

    const drawOptimizedRoute = (route) => {
        if (!googleMapRef.current || !window.google) return;

        // Clear previous directions
        if (directionsRendererRef.current) {
            directionsRendererRef.current.setMap(null);
        }

        const directionsService = new window.google.maps.DirectionsService();
        directionsRendererRef.current = new window.google.maps.DirectionsRenderer({
            map: googleMapRef.current,
            suppressMarkers: true,
            polylineOptions: {
                strokeColor: '#4285F4',
                strokeWeight: 5,
                strokeOpacity: 0.8
            }
        });

        if (route.length === 0) return;

        const waypoints = route.slice(1, -1).map(job => ({
            location: { lat: job.lat, lng: job.lng },
            stopover: true
        }));

        const request = {
            origin: currentLocation,
            destination: { lat: route[route.length - 1].lat, lng: route[route.length - 1].lng },
            waypoints: waypoints,
            optimizeWaypoints: false,
            travelMode: window.google.maps.TravelMode.DRIVING
        };

        directionsService.route(request, (result, status) => {
            if (status === 'OK') {
                directionsRendererRef.current.setDirections(result);
            }
        });

        // Update markers
        addMarkersToMap();
    };

    const handleJobClick = (job) => {
        setSelectedJob(job);
        if (googleMapRef.current) {
            googleMapRef.current.panTo({ lat: job.lat, lng: job.lng });
            googleMapRef.current.setZoom(14);
        }
    };

    const handleCompleteJob = (jobId) => {
        const updatedJobs = jobs.filter(job => job.id !== jobId);
        setJobs(updatedJobs);

        if (isOptimized) {
            setOptimizedRoute(updatedJobs);
            if (updatedJobs.length > 0) {
                drawOptimizedRoute(updatedJobs);
            } else {
                if (directionsRendererRef.current) {
                    directionsRendererRef.current.setMap(null);
                }
            }
        } else {
            addMarkersToMap();
        }

        if (selectedJob?.id === jobId) {
            setSelectedJob(null);
        }
    };

    const startNavigation = (job) => {
        const destination = `${job.lat},${job.lng}`;
        const origin = `${currentLocation.lat},${currentLocation.lng}`;
        const url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&travelmode=driving`;
        window.open(url, '_blank');
    };

    const loadGoogleMapsScript = () => {
        if (window.google) {
            initializeGoogleMap();
            return;
        }

        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,directions`;
        script.async = true;
        script.defer = true;
        script.onload = () => {
            initializeGoogleMap();
        };
        document.head.appendChild(script);
    };

    const handleApiKeySubmit = () => {
        if (apiKey.trim()) {
            setShowApiInput(false);
            loadGoogleMapsScript();
        }
    };

    // ==================== ROUTE SAVING/LOADING ====================

    const loadSavedRoutes = () => {
        const routes = getAllRoutes();
        setSavedRoutes(routes);
    };

    const handleSaveRoute = async (routeName) => {
        try {
            const result = saveRoute(routeName, jobs, isOptimized);

            if (result.success) {
                setCurrentRouteName(routeName);
                setCurrentRouteId(result.id);
                loadSavedRoutes(); // Refresh the list
                return Promise.resolve();
            } else {
                throw new Error(result.error || 'Failed to save route');
            }
        } catch (error) {
            console.error('Error saving route:', error);
            throw error;
        }
    };

    const handleLoadRoute = (routeId) => {
        const route = getRoute(routeId);

        if (route) {
            setJobs(route.jobs);
            setIsOptimized(route.isOptimized);
            setCurrentRouteName(route.name);
            setCurrentRouteId(route.id);

            // Redraw map
            if (route.isOptimized && googleMapRef.current) {
                drawOptimizedRoute(route.jobs);
            } else {
                addMarkersToMap();
            }
        } else {
            alert('Route not found');
        }
    };

    const handleDeleteRoute = async (routeId) => {
        const result = deleteRoute(routeId);

        if (result.success) {
            // If deleting current route, reset
            if (routeId === currentRouteId) {
                setCurrentRouteName(null);
                setCurrentRouteId(null);
            }
            return Promise.resolve();
        } else {
            throw new Error(result.error || 'Failed to delete route');
        }
    };

    return (
        <div className="flex h-full bg-gray-50 font-sans">
            {/* Sidebar */}
            <div
                className={`bg-gradient-to-b from-green-600 to-green-700 text-white transition-all duration-300 ${sidebarCollapsed ? 'w-16' : 'w-96'
                    } flex flex-col shadow-2xl overflow-hidden`}
            >
                {/* Header */}
                <div className="p-6 bg-green-800">
                    <div className="flex items-center justify-between">
                        {!sidebarCollapsed && (
                            <div>
                                <h1 className="text-2xl font-bold tracking-tight">JOBS</h1>
                                <p className="text-green-200 text-sm mt-1">Route Planner</p>
                            </div>
                        )}
                        <button
                            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                            className="p-2 hover:bg-green-700 rounded-lg transition-colors"
                        >
                            <Menu size={24} />
                        </button>
                    </div>

                    {!sidebarCollapsed && (
                        <>
                            <div className="mt-4 bg-green-900/50 rounded-lg px-4 py-2 text-center">
                                <div className="text-sm text-green-200">
                                    {currentRouteName || 'Today'}
                                </div>
                                <div className="text-lg font-semibold">
                                    {new Date().toLocaleDateString('en-US', {
                                        weekday: 'long',
                                        month: 'short',
                                        day: 'numeric'
                                    })}
                                </div>
                            </div>

                            {/* Save/Load Route Buttons */}
                            <div className="mt-4 flex gap-2">
                                <button
                                    onClick={() => setShowSaveModal(true)}
                                    disabled={jobs.length === 0}
                                    className="flex-1 bg-white text-green-700 py-2 rounded-lg font-semibold hover:bg-green-50 transition-colors flex items-center justify-center gap-2 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <Save size={18} />
                                    Save
                                </button>
                                <LoadRouteDropdown
                                    routes={savedRoutes}
                                    onLoad={handleLoadRoute}
                                    onDelete={handleDeleteRoute}
                                    onRefresh={loadSavedRoutes}
                                    currentRouteName={currentRouteName}
                                />
                            </div>
                        </>
                    )}
                </div>

                {!sidebarCollapsed && (
                    <>
                        {/* Jobs List */}
                        <div className="flex-1 overflow-y-auto p-4 space-y-3">
                            {jobs.length === 0 ? (
                                <div className="text-center py-12 text-green-200">
                                    <Check size={48} className="mx-auto mb-3 opacity-50" />
                                    <p className="text-lg">All jobs completed!</p>
                                </div>
                            ) : (
                                jobs.map((job, index) => (
                                    <div
                                        key={job.id}
                                        onClick={() => handleJobClick(job)}
                                        className={`bg-white/10 backdrop-blur-sm rounded-lg p-4 cursor-pointer transition-all hover:bg-white/20 hover:scale-[1.02] ${selectedJob?.id === job.id ? 'ring-2 ring-white bg-white/20' : ''
                                            }`}
                                    >
                                        <div className="flex items-start justify-between mb-2">
                                            <div className="flex items-center gap-3">
                                                {isOptimized && (
                                                    <div className="bg-white text-green-700 rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">
                                                        {index + 1}
                                                    </div>
                                                )}
                                                <div>
                                                    <h3 className="font-semibold text-lg">{job.name}</h3>
                                                    <p className="text-green-100 text-sm">{job.time}</p>
                                                </div>
                                            </div>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleCompleteJob(job.id);
                                                }}
                                                className="p-2 hover:bg-red-500 rounded-full transition-colors"
                                                title="Complete Job"
                                            >
                                                <X size={18} />
                                            </button>
                                        </div>

                                        <div className="space-y-2 text-sm">
                                            <div className="flex items-start gap-2">
                                                <MapPin size={16} className="mt-0.5 flex-shrink-0" />
                                                <span className="text-green-50">{job.address}</span>
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <span className="text-green-100">{job.phone}</span>
                                                <span className="bg-green-800 px-3 py-1 rounded-full text-xs font-mono">
                                                    {job.code}
                                                </span>
                                            </div>
                                        </div>

                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                startNavigation(job);
                                            }}
                                            className="mt-3 w-full bg-white text-green-700 py-2 rounded-lg font-semibold hover:bg-green-50 transition-colors flex items-center justify-center gap-2"
                                        >
                                            <Navigation size={16} />
                                            Start Navigation
                                        </button>
                                    </div>
                                ))
                            )}
                        </div>

                        {/* Optimize Route Button */}
                        {jobs.length > 0 && (
                            <div className="p-4 bg-green-800">
                                <button
                                    onClick={optimizeRoute}
                                    disabled={isOptimized}
                                    className={`w-full py-4 rounded-xl font-bold text-lg transition-all flex items-center justify-center gap-3 ${isOptimized
                                        ? 'bg-green-900 text-green-300 cursor-not-allowed'
                                        : 'bg-white text-green-700 hover:bg-green-50 hover:scale-[1.02] shadow-lg'
                                        }`}
                                >
                                    <RouteIcon size={24} />
                                    {isOptimized ? 'Route Optimized' : 'Optimize Route'}
                                </button>
                                {isOptimized && (
                                    <button
                                        onClick={() => {
                                            setIsOptimized(false);
                                            setOptimizedRoute([]);
                                            if (directionsRendererRef.current) {
                                                directionsRendererRef.current.setMap(null);
                                            }
                                            addMarkersToMap();
                                        }}
                                        className="w-full mt-2 py-2 rounded-lg font-semibold text-sm bg-green-900/50 hover:bg-green-900 transition-colors"
                                    >
                                        Reset Route
                                    </button>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Map Container */}
            <div className="flex-1 relative">
                {showApiInput ? (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
                        <div className="bg-white p-8 rounded-2xl shadow-2xl max-w-md w-full mx-4">
                            <h2 className="text-2xl font-bold text-gray-800 mb-4">
                                Enter Google Maps API Key
                            </h2>
                            <p className="text-gray-600 mb-6">
                                To use Google Maps, please enter your API key. You can get one from the{' '}
                                <a
                                    href="https://developers.google.com/maps/documentation/javascript/get-api-key"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:underline"
                                >
                                    Google Cloud Console
                                </a>
                                .
                            </p>
                            <input
                                type="text"
                                value={apiKey}
                                onChange={(e) => setApiKey(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleApiKeySubmit()}
                                placeholder="Enter your API key"
                                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-green-500 focus:outline-none mb-4"
                            />
                            <button
                                onClick={handleApiKeySubmit}
                                className="w-full bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors"
                            >
                                Load Map
                            </button>
                        </div>
                    </div>
                ) : (
                    <div ref={mapRef} className="w-full h-full" />
                )}

                {/* Job Counter */}
                {!showApiInput && (
                    <div className="absolute bottom-4 right-4 bg-white rounded-lg shadow-lg px-4 py-3 z-[1000]">
                        <div className="flex items-center gap-3">
                            <div className="bg-green-600 text-white rounded-full w-10 h-10 flex items-center justify-center font-bold">
                                {jobs.length}
                            </div>
                            <div className="text-sm">
                                <div className="font-semibold text-gray-800">Active Jobs</div>
                                <div className="text-gray-600">
                                    {isOptimized ? 'Route optimized' : 'Click to optimize'}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Save Route Modal */}
            <SaveRouteModal
                show={showSaveModal}
                onClose={() => setShowSaveModal(false)}
                onSave={handleSaveRoute}
                jobCount={jobs.length}
                isOptimized={isOptimized}
            />
        </div>
    );
};

export default RoutesTab;
